import React, {useState} from "react";
import {Box, CircularProgress, IconButton, Stack, Switch, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Delete02Icon, PencilEdit01Icon, Route01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {AmiRepository} from "../../../repositories/AmiRepository";
import useConfirm from "../../../hooks/useConfirm";
import {getTarget, LATEST_VERSION} from "../../project/dialplan/components/SubProjectFields";
import {DEFAULT_CONTEXT, DEFAULT_TRUNK} from "../../project/dialplan/components/ExtensionAdvancedSettings";
import {PANEL_SX} from "../dashboard/components/Panel";
import {subProjectLabel} from "../../../utils/subProjects";

const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};

const Detail = ({label, children}) => (
  <Box sx={{minWidth: 0}}>
    <Typography sx={{fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0"}}>{label}</Typography>
    <Typography noWrap sx={{fontSize: 12, fontWeight: 500, color: "#23264D", fontFamily: "monospace"}}>{children}</Typography>
  </Box>
);

// One extension of the dialplan: number, name, the sub project / version it runs, status and actions.
// projectId: the registry project, used for every AMI call.
export const ExtensionTile = ({extension, projectId, subProjects, onEdit, onSaved, onDeleted}) => {
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const confirm = useConfirm();

  const target = getTarget(extension, subProjects);
  const language = subProjects.find(item => item.id === target.subProjectId);
  const enabled = extension.enabled;
  const variableCount = Object.keys(extension.variables || {}).length;

  const toggle = () => {
    const enable = !enabled;
    setToggling(true);
    (enable ? AmiRepository.enableExtension(projectId, extension.extension) : AmiRepository.disableExtension(projectId, extension.extension))
      .then((res) => {
        toast.success(`Extension ${extension.extension} ${enable ? "enabled" : "disabled"}`);
        if (res?.data?.astdb_synced === false) toast.warning("Saved, but the live sync to Asterisk failed");
        onSaved({...extension, ...res?.data});
      })
      .catch((err) => toast.error(err?.response?.data?.error || `Failed to ${enable ? "enable" : "disable"} extension`))
      .finally(() => setToggling(false));
  };

  const remove = async () => {
    const confirmed = await confirm({
      title: "Delete extension",
      message: `Delete extension ${extension.extension}? It is removed from the dialplan entirely and cannot be undone.`,
    });
    if (!confirmed) return;
    setDeleting(true);
    AmiRepository.deleteExtensionById(projectId, extension.id)
      .then(() => {
        toast.success(`Extension ${extension.extension} deleted`);
        onDeleted(extension);
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to delete extension"))
      .finally(() => setDeleting(false));
  };

  const status = typeof enabled !== "boolean"
    ? {label: "Unknown", bg: "#F2F2F7", color: "#6B6B80"}
    : enabled ? {label: "Active", bg: "#E8F7EF", color: "#1E8A55"} : {label: "Disabled", bg: "#FDECEC", color: "#C73A3A"};

  return (
    <Box sx={{
      ...PANEL_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.5, cursor: "pointer",
      transition: "box-shadow 0.2s, border-color 0.2s",
      opacity: enabled === false ? 0.85 : 1,
      "&:hover": {boxShadow: "0 10px 30px rgba(35, 38, 77, 0.08)", borderColor: "#DCDCEA"},
    }} onClick={() => onEdit(extension)}>
      {/* Number, name, status */}
      <Stack direction={"row"} alignItems={"flex-start"} gap={1.5}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center",
          justifyContent: "center", color: "#8491F7", bgcolor: "rgba(132, 145, 247, 0.12)",
        }}>
          <HugeiconsIcon icon={Route01Icon} size={19}/>
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", fontFamily: "monospace", letterSpacing: "-0.01em"}}>
              {extension.extension}
            </Typography>
            <Box component={"span"} sx={{
              display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.875, py: 0.125, borderRadius: "999px",
              fontSize: 11, fontWeight: 600, bgcolor: status.bg, color: status.color,
            }}>
              <Box component={"span"} sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor"}}/>
              {status.label}
            </Box>
          </Stack>
          <Typography noWrap sx={{fontSize: 13, fontWeight: 600, color: extension.name ? "#4A4A63" : "#B4B4C3"}}>
            {extension.name || "Unnamed extension"}
          </Typography>
        </Box>
        <Box onClick={(e) => e.stopPropagation()} sx={{flexShrink: 0, mt: -0.5, mr: -1}}>
          {toggling
            ? <CircularProgress size={16} sx={{m: 1.25, color: "#8491F7"}}/>
            : (
              <Tooltip title={enabled ? "Disable extension" : "Enable extension"}>
                <Switch size={"small"} checked={!!enabled} onChange={toggle}/>
              </Tooltip>
            )}
        </Box>
      </Stack>

      <Typography sx={{
        fontSize: 12, color: "#8A8AA0", minHeight: 36, display: "-webkit-box", WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>
        {extension.description || "No description"}
      </Typography>

      {/* What it runs */}
      <Stack direction={"row"} gap={0.75} flexWrap={"wrap"}>
        {language ? (
          <Box component={"span"} sx={{px: 1, py: 0.25, borderRadius: "6px", fontSize: 11, fontWeight: 600, color: "#4A4A63", bgcolor: "#F2F2F7"}}>
            {subProjectLabel(language)}
          </Box>
        ) : (
          <Box component={"span"} sx={{px: 1, py: 0.25, borderRadius: "6px", fontSize: 11, fontWeight: 600, color: "#A86A0C", bgcolor: "#FDF4E3"}}>
            No sub project
          </Box>
        )}
        <Box component={"span"} sx={{
          px: 1, py: 0.25, borderRadius: "6px", fontSize: 11, fontWeight: 600,
          color: target.versionType === "PRODUCTION" ? "#1E8A55" : "#6E5BC4",
          bgcolor: target.versionType === "PRODUCTION" ? "#E8F7EF" : "#F1EEFB",
        }}>
          {target.versionType === "PRODUCTION" ? "Production" : "Stage"} · {target.version === LATEST_VERSION ? "latest" : `#${target.version}`}
        </Box>
      </Stack>

      {/* Routing details */}
      <Box sx={{
        display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1, p: 1.25, borderRadius: "10px",
        bgcolor: "#FAFAFD", border: "1px solid #F0F0F6",
      }}>
        <Detail label={"TRUNK"}>{extension.trunk || DEFAULT_TRUNK}</Detail>
        <Detail label={"CONTEXT"}>{extension.context || DEFAULT_CONTEXT}</Detail>
        <Detail label={"VARIABLES"}>{variableCount}</Detail>
      </Box>

      {enabled === false && extension.redirect_context && extension.redirect_exten && (
        <Typography sx={{fontSize: 12, color: "#A86A0C"}}>
          Calls redirected to <Box component={"span"} sx={{fontFamily: "monospace", fontWeight: 600}}>
          {extension.redirect_exten}@{extension.redirect_context}</Box>
        </Typography>
      )}

      <Stack direction={"row"} justifyContent={"flex-end"} gap={0.25} sx={{mt: "auto"}} onClick={(e) => e.stopPropagation()}>
        <Tooltip title={"Edit"}>
          <IconButton size={"small"} sx={iconButtonSx} onClick={() => onEdit(extension)}>
            <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
          </IconButton>
        </Tooltip>
        <Tooltip title={extension.id ? "Delete" : "No internal id - can't be deleted"}>
          <span>
            <IconButton size={"small"} disabled={!extension.id || deleting} onClick={remove}
                        sx={{...iconButtonSx, "&:hover": {bgcolor: "#FDECEC", color: "#C73A3A"}}}>
              {deleting ? <CircularProgress size={14}/> : <HugeiconsIcon icon={Delete02Icon} size={16}/>}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};
